import React, { useState, useEffect } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Snackbar,
    Alert,
    Autocomplete,
    TextField,
    Box,
    Typography,
    Chip,
    IconButton,
    Tooltip,
    Card,
    CardContent,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Divider,
    Paper,
    CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import httpRequest from "../utils/httpRequest";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';

const MAX_ENTRIES = 10;

const paymentModes = [
    "Cash",
    "UPI",
    "Bank Transfer",
    "Credit Card",
    "Debit Card",
    "Cheque",
    "Paytm",
    "PhonePe",
    "Google Pay",
    "Other",
];

const CommonLedgerEntryForm = ({ open, onClose, onSuccess, singleCustomer = null }) => {
    const [customers, setCustomers] = useState([]);
    const [entries, setEntries] = useState([
        {
            id: 1,
            customer_id: singleCustomer?.customer_id || null,
            customer_name: singleCustomer?.name || "",
            amount: "",
            description: "CASH",
            paymentMode: "Cash",
            date: new Date().toISOString().split("T")[0],
            type: "credit",
        },
    ]);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: "",
        severity: "success",
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (!open) resetForm();
    }, [open]);

    useEffect(() => {
        fetchCustomers();
    }, []);

    // Keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Enter' && event.ctrlKey) {
                event.preventDefault();
                handleSubmit();
            }
        };

        if (open) {
            document.addEventListener('keydown', handleKeyDown);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open, entries]);

    const fetchCustomers = async () => {
        try {
            const response = await httpRequest("customers/getAll", "GET");
            setCustomers(response || []);
        } catch (error) {
            console.error("Failed to fetch customers", error);
        }
    };

    const resetForm = () => {
        setEntries([
            {
                id: 1,
                customer_id: singleCustomer?.customer_id || null,
                customer_name: singleCustomer?.name || "",
                amount: "",
                description: "CASH",
                paymentMode: "Cash",
                date: new Date().toISOString().split("T")[0],
                type: "credit",
            },
        ]);
    };

    const handleAddRow = () => {
        if (entries.length >= MAX_ENTRIES) {
            setSnackbar({
                open: true,
                message: `Maximum ${MAX_ENTRIES} entries allowed at once.`,
                severity: "warning",
            });
            return;
        }
        
        const newId = Math.max(...entries.map(e => e.id)) + 1;
        const newEntry = {
            id: newId,
            customer_id: singleCustomer?.customer_id || null,
            customer_name: singleCustomer?.name || "",
            amount: "",
            description: "CASH",
            paymentMode: "Cash",
            date: new Date().toISOString().split("T")[0],
            type: "credit",
        };
        
        setEntries(prev => [...prev, newEntry]);
    };

    const handleRemoveRow = (id) => {
        if (entries.length > 1) {
            setEntries((prev) => prev.filter((entry) => entry.id !== id));
        }
    };

    const handleUpdateEntry = (id, field, value) => {
        setEntries((prev) =>
            prev.map((entry) =>
                entry.id === id ? { ...entry, [field]: value } : entry
            )
        );
    };

    const handleCopyEntry = (entryToCopy) => {
        if (entries.length >= MAX_ENTRIES) {
            setSnackbar({
                open: true,
                message: `Maximum ${MAX_ENTRIES} entries allowed at once.`,
                severity: "warning",
            });
            return;
        }
        
        const newId = Math.max(...entries.map(e => e.id)) + 1;
        const newEntry = {
            ...entryToCopy,
            id: newId,
            amount: "",
            description: "CASH",
        };
        setEntries(prev => [...prev, newEntry]);
    };

    const handleSubmit = async () => {
        if (entries.length > MAX_ENTRIES) {
            setSnackbar({
                open: true,
                message: `Maximum ${MAX_ENTRIES} entries allowed at once.`,
                severity: "warning",
            });
            return;
        }

        const invalidEntries = entries.filter(entry =>
            (!entry.customer_id && !singleCustomer) ||
            !entry.amount ||
            !entry.paymentMode ||
            !entry.date ||
            !entry.description ||
            isNaN(parseFloat(entry.amount))
        );

        if (invalidEntries.length > 0) {
            setSnackbar({
                open: true,
                message: "Please fill all required fields correctly for all entries.",
                severity: "warning",
            });
            return;
        }

        const entriesByCustomer = entries.reduce((acc, entry) => {
            const customerId = entry.customer_id || (singleCustomer ? singleCustomer.customer_id : null);
            
            if (!customerId) {
                console.error('No customer_id found for entry:', entry);
                return acc;
            }
            
            if (!acc[customerId]) {
                acc[customerId] = [];
            }

            acc[customerId].push({
                type: entry.type,
                amount: parseFloat(entry.amount) || 0,
                description: entry.description,
                payment_mode: entry.paymentMode,
                transaction_date: entry.date,
            });

            return acc;
        }, {});

        setIsSubmitting(true);
        try {
            const customersData = Object.entries(entriesByCustomer).map(([customerId, transactions]) => ({
                customer_id: parseInt(customerId),
                transactions,
            }));

            await httpRequest("customers/transactions/bulk", "POST", {
                customers: customersData,
            });

            setSnackbar({
                open: true,
                message: singleCustomer 
                    ? `Successfully added ${entries.length} ledger entries for ${singleCustomer.name}!`
                    : `Successfully added ${entries.length} ledger entries for ${Object.keys(entriesByCustomer).length} customers!`,
                severity: "success",
            });
            resetForm();
            onSuccess();
        } catch (error) {
            console.error("Failed to add ledger entries", error);
            setSnackbar({
                open: true,
                message: error?.response?.data?.error || "Failed to add ledger entries",
                severity: "error",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const today = new Date();
    const maxDate = today.toISOString().split("T")[0];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="xl"
            fullWidth
            PaperProps={{
                sx: {
                    width: "95vw",
                    maxWidth: "1200px",
                    maxHeight: "85vh",
                    m: 1
                },
            }}
        >
            <DialogTitle sx={{ pb: 0.5, px: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box>
                        <Typography variant="h6" sx={{ mb: 0.25 }}>
                            {singleCustomer ? `Ledger Entry - ${singleCustomer.name}` : 'Common Ledger Entry Form'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            Maximum {MAX_ENTRIES} entries per submission
                        </Typography>
                    </Box>
                </Box>
            </DialogTitle>
            <DialogContent sx={{ pt: 1, px: 2 }}>
                <Box sx={{ mb: 1.5, display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontWeight: 'medium' }}>
                        Quick Actions:
                    </Typography>
                    <Chip
                        label="Today"
                        size="small"
                        variant="outlined"
                        disabled={isSubmitting}
                        onClick={() => {
                            const today = new Date().toISOString().split("T")[0];
                            setEntries(prev => prev.map(entry => ({ ...entry, date: today })));
                        }}
                        sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'action.hover' } }}
                    />
                    <Chip
                        label="Credit"
                        size="small"
                        variant="outlined"
                        color="success"
                        disabled={isSubmitting}
                        onClick={() => {
                            setEntries(prev => prev.map(entry => ({ ...entry, type: "credit" })));
                        }}
                        sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'success.light' } }}
                    />
                    <Chip
                        label="Debit"
                        size="small"
                        variant="outlined"
                        color="error"
                        disabled={isSubmitting}
                        onClick={() => {
                            setEntries(prev => prev.map(entry => ({ ...entry, type: "debit" })));
                        }}
                        sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'error.light' } }}
                    />
                    <Chip
                        label="Clear"
                        size="small"
                        variant="outlined"
                        color="warning"
                        disabled={isSubmitting}
                        onClick={() => {
                            setEntries(prev => prev.map(entry => ({
                                ...entry,
                                customer_id: singleCustomer?.customer_id || null,
                                customer_name: singleCustomer?.name || "",
                                amount: "",
                                description: "CASH",
                                paymentMode: "Cash",
                            })));
                        }}
                        sx={{ cursor: 'pointer', fontSize: '0.7rem', height: '24px', '&:hover': { bgcolor: 'warning.light' } }}
                    />
                </Box>

                <Box sx={{ mb: 1.5, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                    <Grid container spacing={1} alignItems="center">
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography 
                                variant="caption" 
                                fontWeight="bold" 
                                color={entries.length >= MAX_ENTRIES ? 'error.main' : entries.length >= MAX_ENTRIES - 2 ? 'warning.main' : 'text.secondary'}
                            >
                                Summary: {entries.length}/{MAX_ENTRIES} entries {!singleCustomer && `• ${new Set(entries.filter(e => e.customer_id).map(e => e.customer_id)).size} customers`}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
                                <Chip
                                    label={`Credit: ₹${entries.filter(e => e.type === 'credit' && e.amount).reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}`}
                                    color="success"
                                    size="small"
                                    variant="filled"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                                <Chip
                                    label={`Debit: ₹${entries.filter(e => e.type === 'debit' && e.amount).reduce((sum, e) => sum + parseFloat(e.amount), 0).toFixed(2)}`}
                                    color="error"
                                    size="small"
                                    variant="filled"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                                <Chip
                                    label={`Net: ₹${(entries.filter(e => e.type === 'debit' && e.amount).reduce((sum, e) => sum + parseFloat(e.amount), 0) - entries.filter(e => e.type === 'credit' && e.amount).reduce((sum, e) => sum + parseFloat(e.amount), 0)).toFixed(2)}`}
                                    color="info"
                                    size="small"
                                    variant="outlined"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            </Box>
                        </Grid>
                    </Grid>
                    <Box sx={{ mt: 0.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="caption" color="text.secondary">
                            {entries.filter(e => (!e.customer_id && !singleCustomer) || !e.amount || !e.paymentMode || !e.date || !e.description).length} incomplete entries
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ maxHeight: '65vh', overflowY: 'auto' }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            p: 1,
                            bgcolor: 'grey.100',
                            borderBottom: '2px solid #e0e0e0',
                            fontWeight: 'bold',
                            fontSize: '0.75rem',
                            color: 'text.secondary',
                            position: 'sticky',
                            top: 0,
                            zIndex: 1,
                        }}
                    >
                        <Box sx={{ minWidth: '40px', textAlign: 'center' }}>#</Box>
                        {!singleCustomer && <Box sx={{ flex: 2, minWidth: 0 }}>Customer</Box>}
                        <Box sx={{ flex: 1, minWidth: '120px' }}>Amount</Box>
                        <Box sx={{ flex: 0.5, minWidth: '80px' }}>Type</Box>
                        <Box sx={{ flex: 1, minWidth: '120px' }}>Payment</Box>
                        <Box sx={{ flex: 1, minWidth: '120px' }}>Date</Box>
                        <Box sx={{ flex: 1.5, minWidth: '150px' }}>Description</Box>
                        <Box sx={{ minWidth: '80px', textAlign: 'center' }}>Actions</Box>
                    </Box>

                    {entries.map((entry, index) => (
                        <Box
                            key={entry.id}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1,
                                borderBottom: '1px solid #e0e0e0',
                                bgcolor: index % 2 === 0 ? 'background.paper' : 'grey.50',
                                '&:hover': { bgcolor: 'action.hover' },
                                minHeight: '48px',
                                position: 'relative',
                            }}
                        >
                            <Box sx={{ minWidth: '40px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                                    #{index + 1}
                                </Typography>
                                <Tooltip title={((singleCustomer || entry.customer_id) && entry.amount && entry.paymentMode && entry.date && entry.description) ?
                                    `Row ${index + 1} - Complete` :
                                    `Row ${index + 1} - Incomplete (missing: ${[
                                        (!entry.customer_id && !singleCustomer) && 'customer',
                                        !entry.amount && 'amount',
                                        !entry.paymentMode && 'payment mode',
                                        !entry.date && 'date',
                                        !entry.description && 'description'
                                    ].filter(Boolean).join(', ')})`
                                }>
                                    <Box
                                        sx={{
                                            width: 6,
                                            height: 6,
                                            borderRadius: '50%',
                                            bgcolor: ((singleCustomer || entry.customer_id) && entry.amount && entry.paymentMode && entry.date && entry.description) ? 'success.main' : 'warning.main',
                                            cursor: 'help',
                                            flexShrink: 0
                                        }}
                                    />
                                </Tooltip>
                            </Box>

                            {!singleCustomer && (
                                <Box sx={{ flex: 2, minWidth: 0 }}>
                                    <Autocomplete
                                        options={customers}
                                        getOptionLabel={(option) =>
                                            `${option.name} (${option.account_number || "No A/C"})`
                                        }
                                        value={customers.find(c => c.id === entry.customer_id) || null}
                                        onChange={(event, newValue) => {
                                            handleUpdateEntry(entry.id, "customer_id", newValue ? newValue.id : null);
                                            handleUpdateEntry(entry.id, "customer_name", newValue ? newValue.name : "");
                                        }}
                                        disabled={isSubmitting}
                                        renderOption={(props, option) => (
                                            <Box component="li" {...props}>
                                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                                        {option.name} ({option.account_number || "No A/C"})
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                                        <LocationOnIcon sx={{ fontSize: '0.75rem', color: 'text.secondary' }} />
                                                        <Typography variant="caption" color="text.secondary">
                                                            {option.location || "No location"}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            </Box>
                                        )}
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                placeholder="Select customer"
                                                size="small"
                                                error={!entry.customer_id}
                                                sx={{
                                                    '& .MuiInputBase-root': {
                                                        bgcolor: 'background.paper',
                                                        '&:hover': { bgcolor: 'action.hover' }
                                                    }
                                                }}
                                            />
                                        )}
                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                        sx={{ '& .MuiAutocomplete-input': { fontSize: '0.875rem' } }}
                                    />
                                </Box>
                            )}

                            <Box sx={{ flex: 1, minWidth: '100px' }}>
                                <TextField
                                    placeholder="Amount"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={entry.amount}
                                    onChange={(e) => handleUpdateEntry(entry.id, "amount", e.target.value)}
                                    error={!entry.amount}
                                    disabled={isSubmitting}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{ flex: 0.5, minWidth: '100px' }}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={entry.type}
                                        onChange={(e) => handleUpdateEntry(entry.id, "type", e.target.value)}
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <MenuItem value="credit">Credit</MenuItem>
                                        <MenuItem value="debit">Debit</MenuItem>
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: '160px' }}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={entry.paymentMode}
                                        onChange={(e) => handleUpdateEntry(entry.id, "paymentMode", e.target.value)}
                                        error={!entry.paymentMode}
                                        displayEmpty
                                        disabled={isSubmitting}
                                        sx={{
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }}
                                    >
                                        <MenuItem value="" disabled>
                                            <em>Payment Mode</em>
                                        </MenuItem>
                                        {paymentModes.map((mode) => (
                                            <MenuItem key={mode} value={mode}>
                                                {mode}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>

                            <Box sx={{ flex: 1, minWidth: '120px' }}>
                                <DatePicker
                                    value={entry.date ? dayjs(entry.date) : null}
                                    onChange={(newValue) => {
                                        handleUpdateEntry(entry.id, "date", newValue?.format("YYYY-MM-DD") || "");
                                    }}
                                    disabled={isSubmitting}
                                    slotProps={{
                                        textField: {
                                            size: "small",
                                            fullWidth: true,
                                            error: !entry.date,
                                            placeholder: "Date",
                                            sx: {
                                                '& .MuiInputBase-root': {
                                                    bgcolor: 'background.paper',
                                                    '&:hover': { bgcolor: 'action.hover' }
                                                }
                                            }
                                        },
                                    }}
                                    format="DD-MM-YYYY"
                                />
                            </Box>

                            <Box sx={{ flex: 1.5, minWidth: '150px' }}>
                                <TextField
                                    placeholder="Description"
                                    size="small"
                                    fullWidth
                                    value={entry.description}
                                    onChange={(e) => handleUpdateEntry(entry.id, "description", e.target.value)}
                                    error={!entry.description}
                                    disabled={isSubmitting}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            bgcolor: 'background.paper',
                                            '&:hover': { bgcolor: 'action.hover' }
                                        }
                                    }}
                                />
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: '80px' }}>
                                <Tooltip title={entries.length >= MAX_ENTRIES ? "Maximum entries reached" : "Copy entry"}>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleCopyEntry(entry)}
                                        disabled={entries.length >= MAX_ENTRIES || isSubmitting}
                                        sx={{ 
                                            color: entries.length >= MAX_ENTRIES ? 'text.disabled' : 'primary.main', 
                                            p: 0.25 
                                        }}
                                    >
                                        <ContentCopyIcon sx={{ fontSize: '0.875rem' }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Remove entry">
                                    <IconButton
                                        size="small"
                                        onClick={() => handleRemoveRow(entry.id)}
                                        disabled={entries.length <= 1 || isSubmitting}
                                        sx={{ color: 'error.main', p: 0.25 }}
                                    >
                                        <DeleteIcon sx={{ fontSize: '0.875rem' }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Box>
                    ))}

                    <Box sx={{ p: 0.5, borderTop: '1px solid #e0e0e0', bgcolor: 'background.paper', display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            variant="text"
                            startIcon={<AddIcon sx={{ fontSize: '0.875rem' }} />}
                            onClick={handleAddRow}
                            disabled={entries.length >= MAX_ENTRIES || isSubmitting}
                            size="small"
                            sx={{
                                fontSize: '0.7rem',
                                py: 0.25,
                                px: 1,
                                minWidth: 'auto',
                                color: entries.length >= MAX_ENTRIES || isSubmitting ? 'text.disabled' : 'primary.main',
                                '&:hover': entries.length >= MAX_ENTRIES || isSubmitting ? {} : { bgcolor: 'primary.light', color: 'white' }
                            }}
                        >
                            Add {entries.length >= MAX_ENTRIES && '(Max reached)'}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 2, py: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                        💡 Use copy button to duplicate entries quickly
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button onClick={onClose} variant="outlined" size="small" disabled={isSubmitting}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={
                            isSubmitting ||
                            entries.length > MAX_ENTRIES ||
                            entries.filter(e => (!e.customer_id && !singleCustomer) || !e.amount || !e.paymentMode || !e.date || !e.description).length > 0
                        }
                        size="small"
                        startIcon={isSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                        title={
                            isSubmitting
                                ? 'Submitting...'
                                : entries.length > MAX_ENTRIES 
                                ? `Too many entries (${entries.length}/${MAX_ENTRIES})`
                                : entries.filter(e => (!e.customer_id && !singleCustomer) || !e.amount || !e.paymentMode || !e.date || !e.description).length > 0
                                ? `${entries.filter(e => (!e.customer_id && !singleCustomer) || !e.amount || !e.paymentMode || !e.date || !e.description).length} entries have missing required fields`
                                : 'Ready to submit'
                        }
                    >
                        {isSubmitting ? 'Submitting...' : `Submit (${entries.length})`}
                    </Button>
                </Box>
            </DialogActions>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
            >
                <Alert
                    onClose={() => setSnackbar({ ...snackbar, open: false })}
                    severity={snackbar.severity}
                    sx={{ width: "100%" }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Dialog>
    );
};

export default CommonLedgerEntryForm; 